export type PrefillPractice = {
  id: string;
  providers?: Array<{ id: string }>;
};

export type StalePracticeProviderOmitResult<T> = {
  data: T;
  droppedPractice: boolean;
  droppedProvider: boolean;
};

const PRACTICE_ID_KEY = "practice_id";

const PROVIDER_ID_KEYS = [
  "billing_provider_id",
  "rendering_provider_id",
  "provider_id",
] as const;

const PRACTICE_AND_PROVIDER_ID_KEYS = [
  PRACTICE_ID_KEY,
  ...PROVIDER_ID_KEYS,
] as const;

function isSelectableId(value: unknown): value is string {
  return typeof value === "string" && value !== "" && value !== "OTHER";
}

function withoutPracticeAndProviderIds<T extends Record<string, any>>(data: T): T {
  const next: T = { ...data };
  for (const key of PRACTICE_AND_PROVIDER_ID_KEYS) {
    if (key in next) {
      delete next[key];
    }
  }
  return next;
}

/**
 * Never copy practice/provider IDs unless they are on the team's current
 * (non-stale) profile. If the live list is not loaded yet, omit those IDs
 * rather than copying them unverified.
 */
export function omitStalePracticeAndProviderIds<T extends Record<string, any>>(
  data: T,
  practices: PrefillPractice[] | undefined | null
): StalePracticeProviderOmitResult<T> {
  if (practices == null) {
    return {
      data: withoutPracticeAndProviderIds(data),
      droppedPractice: false,
      droppedProvider: false,
    };
  }

  const next: T = { ...data };
  let droppedPractice = false;
  let droppedProvider = false;

  const livePracticeIds = new Set(practices.map((practice) => practice.id));
  const liveProviderIds = new Set(
    practices.flatMap((practice) =>
      (practice.providers ?? []).map((provider) => provider.id)
    )
  );

  if (isSelectableId(next.practice_id) && !livePracticeIds.has(next.practice_id)) {
    delete next.practice_id;
    droppedPractice = true;
  }

  for (const key of PROVIDER_ID_KEYS) {
    if (isSelectableId(next[key]) && !liveProviderIds.has(next[key])) {
      delete next[key];
      droppedProvider = true;
    }
  }

  if (droppedPractice) {
    for (const key of PROVIDER_ID_KEYS) {
      if (next[key] !== undefined) {
        delete next[key];
        droppedProvider = true;
      }
    }
  }

  return { data: next, droppedPractice, droppedProvider };
}
